import { type ThreeElements, useFrame } from '@react-three/fiber';
import { createContext, type ReactNode, useContext, useRef } from 'react';
import type * as THREE from 'three';
import { type Anim, animMatrix } from './anim';

/**
 * Whether animation wrappers move their groups. Off while a model is baked
 * into instanced parts (`ModelBatcher`): there the shader moves them, and the
 * bake needs the rest pose.
 */
export const AnimateContext = createContext(true);

type GroupProps = Pick<ThreeElements['group'], 'position' | 'rotation' | 'scale'>;

/**
 * A group whose children move as `anim` describes, about the group's origin
 * (its `position`/`rotation`/`scale` place that origin). The inner group
 * carries `userData.anim`, which is how a bake finds it.
 */
export function Animated({
  anim,
  children,
  ...placement
}: GroupProps & { anim: Anim; children?: ReactNode }) {
  const inner = useRef<THREE.Group>(null);
  const animate = useContext(AnimateContext);
  useFrame(({ clock }) => {
    const group = inner.current;
    if (!animate || !group) return;
    animMatrix(anim, clock.elapsedTime, group.matrix);
    group.matrixWorldNeedsUpdate = true;
  });
  return (
    <group {...placement}>
      <group ref={inner} matrixAutoUpdate={false} userData={{ anim }}>
        {children}
      </group>
    </group>
  );
}

/** Turns about `axis` at `speed` rad/s (sails, wheels, radar dishes, rotors). */
export function Spin({
  axis,
  speed,
  children,
  ...placement
}: GroupProps & { axis: 'x' | 'y' | 'z'; speed: number; children?: ReactNode }) {
  const anim = useStableAnim(`spin:${axis}:${speed}`, () => ({ spin: { axis, speed } }));
  return (
    <Animated anim={anim} {...placement}>
      {children}
    </Animated>
  );
}

/** Breathes in scale: `base + amount · sin(speed · t + phase)` (`rectify`: blinks). */
export function Pulse({
  amount,
  speed,
  base = 1,
  phase = 0,
  rectify = false,
  children,
  ...placement
}: GroupProps & {
  amount: number;
  speed: number;
  base?: number;
  phase?: number;
  rectify?: boolean;
  children?: ReactNode;
}) {
  const anim = useStableAnim(`pulse:${amount}:${speed}:${base}:${phase}:${rectify}`, () => ({
    pulse: { amount, speed, base, phase, rectify },
  }));
  return (
    <Animated anim={anim} {...placement}>
      {children}
    </Animated>
  );
}

/** Rises and falls `amount` at `speed` rad/s, swaying `yaw` radians at half that rate. */
export function Bob({
  amount,
  speed,
  phase = 0,
  yaw = 0,
  children,
  ...placement
}: GroupProps & {
  amount: number;
  speed: number;
  phase?: number;
  yaw?: number;
  children?: ReactNode;
}) {
  const anim = useStableAnim(`bob:${amount}:${speed}:${phase}:${yaw}`, () => ({
    bob: { amount, speed, phase, yaw },
  }));
  return (
    <Animated anim={anim} {...placement}>
      {children}
    </Animated>
  );
}

/** The same anim object while its parameters stay the same (it lands in `userData`). */
function useStableAnim(key: string, make: () => Anim): Anim {
  const ref = useRef<{ key: string; anim: Anim } | null>(null);
  if (ref.current?.key !== key) ref.current = { key, anim: make() };
  return ref.current.anim;
}
